/**
 * Periodic refresh of the counters on the administration dashboard.
 */

import { query, queryAll } from "../core/dom";
import { getJson } from "../core/http";
import type { StatisticsKey, StatisticsResponse } from "../core/types";
import { STATISTICS_KEYS } from "../core/types";

const REFRESH_MS = 60_000;

/** Return whether `value` is a known statistics key. */
export function isStatisticsKey(value: string): value is StatisticsKey {
    return (STATISTICS_KEYS as readonly string[]).includes(value);
}

/** Write the values of `statistics` into the elements of `root`. */
export function applyStatistics(root: ParentNode, statistics: StatisticsResponse): number {
    let updated = 0;

    for (const element of queryAll<HTMLElement>("[data-stat]", root)) {
        const key = element.dataset["stat"] ?? "";

        if (!isStatisticsKey(key)) {
            continue;
        }

        element.textContent = String(statistics[key]);
        updated += 1;
    }

    return updated;
}

/** Start the refresh loop of the dashboard counters. */
export function initStatistics(): (() => void) | undefined {
    const root = query<HTMLElement>("[data-statistics]");
    if (!root) {
        return undefined;
    }

    const endpoint = root.dataset["statistics"] ?? "";
    if (!endpoint) {
        return undefined;
    }

    const refresh = (): void => {
        void getJson<StatisticsResponse>(endpoint)
            .then((payload) => applyStatistics(root, payload))
            .catch(() => undefined);
    };

    const timer = window.setInterval(refresh, REFRESH_MS);

    return () => window.clearInterval(timer);
}
